import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useWizard } from "../../context/WizardContext";
import { useTranslation } from "../../i18n/useTranslation";
import { isPresignedHttpUrl, resolveReferencePhotoUrl } from "../../utils/presignedImageUrl";
import ImageUploadConsentDialog from "./ImageUploadConsentDialog";
import WizardLayout from "./WizardLayout";
import AdminPageNavigator from "../AdminPageNavigator";
import { API_ORIGIN_BASE_URL } from "../../config/apiBaseUrl";

const TOTAL_STEPS = 5;
const CURRENT_STEP = 3;
const MAX_PHOTOS = 10;

const TIPS = [
  { icon: "😊", titleKey: "wizard.photos.tipFaceTitle", descKey: "wizard.photos.tipFaceDesc" },
  { icon: "🌟", titleKey: "wizard.photos.tipPeopleTitle", descKey: "wizard.photos.tipPeopleDesc" },
  { icon: "💡", titleKey: "wizard.photos.tipLightTitle", descKey: "wizard.photos.tipLightDesc" },
];

const Step2_Images: React.FC = () => {
  const baseUrl = API_ORIGIN_BASE_URL;

  const { images, addImage, removeImage, updateImageTags, uploadImages, persistImageTags, isLoading, error, clearError } = useWizard();
  const { user, token, resetImageUploadConsent, acceptImageUploadConsent } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [r2PreviewByIndex, setR2PreviewByIndex] = useState<Record<number, string>>({});
  const [consentGateReady, setConsentGateReady] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [previewHintsOpenIndex, setPreviewHintsOpenIndex] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const previewHintWrapRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  useEffect(() => {
    let alive = true;
    setConsentGateReady(false);
    (async () => {
      try {
        await resetImageUploadConsent();
      } catch {
        // Keep UI usable even when reset endpoint is temporarily unavailable.
      } finally {
        if (alive) setConsentGateReady(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [location.key, resetImageUploadConsent]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const next: Record<number, string> = {};
      for (let i = 0; i < images.length; i += 1) {
        const img = images[i];
        if (!img?.storageKey) continue;
        const p = img.preview?.trim() || "";
        if (p.startsWith("data:") || p.startsWith("blob:")) continue;
        const needsR2 = !p || (p.startsWith(baseUrl) && p.includes("/uploads/"));
        if (!needsR2) continue;
        const url = await resolveReferencePhotoUrl(
          {
            storageKey: img.storageKey,
            publicUrl: undefined,
            path: undefined,
            filename: img.serverId,
          },
          token
        );
        if (url) next[i] = url;
      }
      if (!cancelled) setR2PreviewByIndex(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [images, token, baseUrl]);

  useEffect(() => {
    if (previewHintsOpenIndex === null) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const el = previewHintWrapRefs.current.get(previewHintsOpenIndex);
      const t = e.target;
      if (t instanceof Node && el && !el.contains(t)) {
        setPreviewHintsOpenIndex(null);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPreviewHintsOpenIndex(null);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [previewHintsOpenIndex]);

  const showUploadConsentGate = consentGateReady && Boolean(user) && !user?.imageUploadConsentAt;
  const blockPageInteraction = !consentGateReady || showUploadConsentGate;

  useEffect(() => {
    if (blockPageInteraction) setPreviewHintsOpenIndex(null);
  }, [blockPageInteraction]);

  const fillPct = (images.length / MAX_PHOTOS) * 100;

  const addFiles = useCallback(
    (incoming: FileList | File[]) => {
      if (blockPageInteraction || !user?.imageUploadConsentAt) return;

      const remaining = MAX_PHOTOS - images.length;
      const toAdd = Array.from(incoming)
        .filter((file) => file.type.startsWith("image/"))
        .slice(0, remaining);

      toAdd.forEach((file) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          addImage({
            file,
            preview: event.target?.result as string,
            tags: "",
            uploaded: false,
          });
        };
        reader.readAsDataURL(file);
      });
    },
    [addImage, blockPageInteraction, images.length, user]
  );

  const getThumbSrc = useCallback(
    (index: number) => {
      const image = images[index];
      const p = image.preview?.trim() || "";
      const r2 = r2PreviewByIndex[index];
      const brokenLocal = Boolean(p) && p.startsWith(baseUrl) && p.includes("/uploads/");
      let resolvedSrc = "";
      if (r2) resolvedSrc = r2;
      else if (p.startsWith("data:") || p.startsWith("blob:")) resolvedSrc = p;
      else if (p && !brokenLocal) resolvedSrc = p;
      else if (image.serverId) resolvedSrc = `${baseUrl}/uploads/${image.serverId}`;

      if (!resolvedSrc) return "";
      if (resolvedSrc.startsWith("data:") || isPresignedHttpUrl(resolvedSrc)) return resolvedSrc;

      const cacheBuster = encodeURIComponent(String(image.serverId || index));
      return `${resolvedSrc}${resolvedSrc.includes("?") ? "&" : "?"}t=${cacheBuster}`;
    },
    [baseUrl, images, r2PreviewByIndex]
  );

  const canContinue = useMemo(() => !isLoading && images.length > 0, [isLoading, images.length]);

  const parseNameRoleNickname = (tags: string): { name: string; role: string; nickname: string } => {
    if (!tags) return { name: "", role: "", nickname: "" };
    const nameMatch = tags.match(/name:\s*([^,]+)/i);
    const roleMatch = tags.match(/role:\s*([^,]+)/i);
    const nicknameMatch = tags.match(/nickname:\s*([^,]+)/i);
    return {
      name: nameMatch ? nameMatch[1] : "",
      role: roleMatch ? roleMatch[1] : "",
      nickname: nicknameMatch ? nicknameMatch[1] : "",
    };
  };

  const formatNameRoleNickname = (name: string, role: string, nickname: string): string => {
    const parts: string[] = [];
    if (name.trim()) parts.push(`Name: ${name}`);
    if (role.trim()) parts.push(`Role: ${role}`);
    if (nickname.trim()) parts.push(`Nickname: ${nickname}`);
    return parts.join(", ");
  };

  const handleContinue = async () => {
    if (!user?.imageUploadConsentAt) {
      setValidationError(t("wizard.photos.agreeToUploadFirst"));
      return;
    }
    if (images.length === 0) {
      setValidationError(t("wizard.photos.needOneImage"));
      return;
    }

    setValidationError("");
    clearError();

    const success = await uploadImages();
    if (!success) return;
    await persistImageTags();
    navigate("/create/story-details");
  };

  return (
    <WizardLayout currentStep={CURRENT_STEP} totalSteps={TOTAL_STEPS} stageClassName="photos-upload-stage">
      <ImageUploadConsentDialog open={showUploadConsentGate} onAccepted={acceptImageUploadConsent} />

      {!consentGateReady && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/25 px-4 backdrop-blur-[1px]"
          role="status"
          aria-live="polite"
        >
          <p className="rounded-2xl bg-white/95 px-6 py-4 text-center text-body shadow-card">{t("wizard.photos.preparingConsent")}</p>
        </div>
      )}

      <main
        id="main-content"
        tabIndex={-1}
        className={`relative z-10 flex w-full flex-1 flex-col items-center justify-center outline-none ${
          blockPageInteraction ? "pointer-events-none select-none" : ""
        }`}
        aria-hidden={blockPageInteraction || undefined}
      >
        {error && (
          <div
            role="alert"
            aria-live="polite"
            className="mb-4 w-full max-w-4xl rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-center text-red-700 shadow-soft"
          >
            <span className="block">{error}</span>
            <button
              type="button"
              onClick={clearError}
              className="mt-2 text-sm font-semibold text-red-700 hover:text-red-900 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 rounded"
            >
              {t("wizard.common.dismissError")}
            </button>
          </div>
        )}

        {validationError && (
          <div
            role="alert"
            aria-live="polite"
            className="mb-4 w-full max-w-4xl rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-center text-amber-900 shadow-soft"
          >
            {validationError}
          </div>
        )}

        <div className="lang-card-root max-w-5xl">
          <p className="lang-eyebrow">{t("wizard.step", { current: CURRENT_STEP, total: TOTAL_STEPS })}</p>
          <h1 className="lang-title">{t("wizard.photos.title", { count: images.length })}</h1>
          <AdminPageNavigator align="center" />
          <p className="lang-sub">{t("wizard.photos.subtitle")}</p>

          <div className="mb-6 flex items-center justify-center gap-3">
            <div className="upload-counter-pill">
              <span
                className="upload-counter-num"
                dir="ltr"
                style={{ unicodeBidi: "isolate" }}
              >
                {images.length}/{MAX_PHOTOS}
              </span>
              <span> {t("wizard.photos.photosLabel")}</span>
            </div>
            <div className="upload-counter-bar">
              <div className="upload-counter-bar-fill" style={{ width: `${fillPct}%` }} />
            </div>
          </div>

          {images.length < MAX_PHOTOS && (
            <button
              type="button"
              className={`dropzone${isDragging ? " drag-over" : ""}`}
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragging(false);
                addFiles(e.dataTransfer.files);
              }}
              disabled={blockPageInteraction || isLoading}
            >
              <div className="dropzone-icon" aria-hidden="true">
                <svg width="26" height="26" viewBox="0 0 28 28" fill="none" stroke="white" strokeWidth="1.8">
                  <path d="M14 4v14M8 10l6-6 6 6" />
                  <path d="M4 20v1a3 3 0 003 3h14a3 3 0 003-3v-1" />
                </svg>
              </div>
              <p className="font-bold text-[#2C3E50]">{t("wizard.photos.dropTitle")}</p>
              <p className="text-xs text-[#4B5563] opacity-60">{t("wizard.photos.dropHint")}</p>
            </button>
          )}

          <input
            ref={inputRef}
            type="file"
            multiple
            accept="image/*"
            className="sr-only"
            onChange={(e) => {
              if (e.target.files) addFiles(e.target.files);
              e.target.value = "";
            }}
            disabled={blockPageInteraction || isLoading}
          />

          {images.length > 0 && (
            <>
              <div className="photo-editors">
                {images.map((image, index) => {
                  const src = getThumbSrc(index);
                  const fallback = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
                  const { name, role, nickname } = parseNameRoleNickname(image.tags || "");
                  return (
                    <div key={`meta-${image.serverId || image.preview || "img"}-${index}`} className="photo-editor-card">
                      <button
                        type="button"
                        className="photo-editor-remove-btn"
                        onClick={() => removeImage(index)}
                        aria-label={t("wizard.photos.removeImageAria")}
                        title={t("wizard.photos.removeImageTitle")}
                        disabled={isLoading}
                      >
                        ✕
                      </button>
                      <div
                        className="photo-editor-preview-wrap"
                        ref={(el) => {
                          if (el) previewHintWrapRefs.current.set(index, el);
                          else previewHintWrapRefs.current.delete(index);
                        }}
                      >
                        <img className="photo-editor-preview" src={src || fallback} alt="" aria-hidden="true" />
                        <button
                          type="button"
                          className="photo-editor-info-btn"
                          aria-label={t("wizard.photos.previewHintsButtonAria")}
                          aria-expanded={previewHintsOpenIndex === index}
                          aria-controls={`photo-hints-popover-${index}`}
                          id={`photo-hints-trigger-${index}`}
                          disabled={blockPageInteraction || isLoading}
                          onClick={() =>
                            setPreviewHintsOpenIndex((prev) => (prev === index ? null : index))
                          }
                        >
                          i
                        </button>
                        {previewHintsOpenIndex === index && (
                          <div
                            className="photo-editor-hints-popover"
                            id={`photo-hints-popover-${index}`}
                          >
                            <span className="photo-editor-hints-heading">{t("wizard.photos.roleLabel")}</span>
                            <p>{t("wizard.photos.roleHint")}</p>
                            <span className="photo-editor-hints-heading">{t("wizard.photos.nicknameLabel")}</span>
                            <p>{t("wizard.photos.nicknameCapitalHint")}</p>
                          </div>
                        )}
                      </div>
                      <div className="photo-editor-fields">
                        <label htmlFor={`photo-name-${index}`}>{t("wizard.photos.nameLabel")}</label>
                        <input
                          id={`photo-name-${index}`}
                          type="text"
                          placeholder={t("wizard.photos.namePlaceholder")}
                          value={name}
                          onChange={(e) => updateImageTags(index, formatNameRoleNickname(e.target.value, role, nickname))}
                          disabled={isLoading}
                        />
                        <div className="photo-field-label-row photo-hover-hint">
                          <label htmlFor={`photo-role-${index}`}>{t("wizard.photos.roleLabel")}</label>
                          <span className="photo-inline-hint" role="note">
                            {t("wizard.photos.roleHint")}
                          </span>
                        </div>
                        <input
                          id={`photo-role-${index}`}
                          type="text"
                          placeholder={t("wizard.photos.rolePlaceholder")}
                          value={role}
                          onChange={(e) => updateImageTags(index, formatNameRoleNickname(name, e.target.value, nickname))}
                          disabled={isLoading}
                        />
                        <div className="photo-field-label-row photo-hover-hint">
                          <label htmlFor={`photo-nickname-${index}`}>{t("wizard.photos.nicknameLabel")}</label>
                          <span className="photo-inline-hint" role="note">
                            {t("wizard.photos.nicknameCapitalHint")}
                          </span>
                        </div>
                        <input
                          id={`photo-nickname-${index}`}
                          type="text"
                          placeholder={t("wizard.photos.nicknamePlaceholder")}
                          value={nickname}
                          onChange={(e) => updateImageTags(index, formatNameRoleNickname(name, role, e.target.value))}
                          disabled={isLoading}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          <div className="upload-tips">
            {TIPS.map((tip) => (
              <div key={tip.titleKey} className="upload-tip">
                <span aria-hidden="true">{tip.icon}</span>
                <div className="upload-tip-text">
                  <strong>{t(tip.titleKey)}</strong>
                  {t(tip.descKey)}
                </div>
              </div>
            ))}
          </div>

          <div className="wizard-nav-row">
            <button type="button" className="wizard-btn-back" onClick={() => navigate("/create/story-type")} disabled={isLoading}>
              ← {t("wizard.common.back")}
            </button>
            <button type="button" className="wizard-btn-next" disabled={!canContinue} onClick={handleContinue}>
              {isLoading ? t("wizard.common.uploading") : t("wizard.common.next")} →
            </button>
          </div>

        </div>
      </main>
    </WizardLayout>
  );
};

export default Step2_Images;